import { Button, Drawer, Flex, Form, Input, Typography } from 'antd';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { selectCurrentAccountId } from 'shared/config/store/selectors';
import { useSignVirtualCardMutation } from '../api';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { ApiError } from 'shared/model/types';
import { useLazyGetAccountsQuery } from 'entities/account/api';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { useLazyGetMonthSpendQuery } from 'widgets/transactions/api';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { GTAG_ID } from 'shared/const/app';
import { useSaveGtagMutation } from 'entities/user/api';
import { general, generalFields } from 'shared/model/schemas/rules';

type Props = {
  open: boolean;
  onClose: () => unknown;
}

type SignForm = {
  '3ds': string;
}

export const GetVirtualCard: FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const [form] = Form.useForm();

  const accountId = useSelector(selectCurrentAccountId);
  const currentEmi = useSelector(getCurrentEmi);
  const userMeta = useSelector(getUserMeta);

  const dispatch = useDispatch();
  const { showError, showSuccess } = useMessage();

  const [signVirtualCard] = useSignVirtualCardMutation();
  const [getAccounts] = useLazyGetAccountsQuery();
  const [fetchMonthSpend] = useLazyGetMonthSpendQuery();
  const [saveGtag] = useSaveGtagMutation();

  const onSubmit = (values: SignForm) => {
    dispatch(setActivity(true));
    signVirtualCard({ accountId, password: values['3ds'] })
      .unwrap()
      .then(async (response) => {
        gtag('get', GTAG_ID, 'client_id', (clientID) => {
          saveGtag({ clientId: clientID as string, eventName: 'web_create_virtual_card', gtag: GTAG_ID });
          gtag('event', 'web_create_virtual_card', {
            user_id: userMeta.id,
            account_id: accountId,
            card_id: response.response.id,
          });
        });
        try {
          await getAccounts({ emi: currentEmi });
        } catch (e) {
          console.log(e);
        }
        try {
          await fetchMonthSpend({ accountId, start: moment().startOf('month').format('DD-MM-YYYY') });
        } catch (e) {
          console.log(e);
        }
        showSuccess?.(t({ id: 'card.virtual.messages.success' }));
        form.resetFields();
        onClose();
      })
      .catch((err: ApiError) => {
        showError?.(t({ id: 'card.virtual.messages.error' }));
        console.log(err);
      })
      .finally(() => {
        dispatch(setActivity(false));
      })
  }

  return (
    <Drawer
      title={t({ id: 'river.views.plan.getVirtualCard' })}
      open={open}
      closable
      onClose={onClose}
      width={560}>
      <Flex vertical gap={16}>
        <Typography.Text>
          {t({ id: 'labels.3ds' })}</Typography.Text>
        <Form
          form={form}
          onFinish={onSubmit}
          className='w100'
        >
          <Flex vertical align='center'>
           <Form.Item
              className='w100'
              name='3ds'
              rules={[general(t)['required-field'], ...generalFields(t)['3ds']]}
            >
              <Input.Password
                placeholder={t({ id: 'fields.3ds' })}
              />
            </Form.Item>
            <Button
              htmlType='submit'
              variant='solid'
              color='default'
            >
              {t({ id: 'buttons.continue' })}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Drawer>
  );
}